import React from "react";
import { Column, Heading, Paragraph, Row } from "@hex-insights/core";
import { EditMode, Form, SubmitButton, useInternalFields } from "@hex-insights/forms";
import { useActivePageRegistration, useHistory } from "@hex-insights/router";
import { ButtonLink, UserDetailQuery, UserField, UserForm, useUserDetailQuery } from "@s-i-s-i/see-learning.shared";
import { AuthenticationContext } from "../../../Contexts";
import { OnboardingLayout } from "../Components";
import { onboardingNotificationsPageInfo } from "../NotificationsPage/pageinfo";
import { onboardingProfilePageInfo } from "../ProfilePage/pageinfo";
import { onboardingDisplayNamePageInfo } from "./pageinfo";

export function OnboardingDisplayNamePage() {
	useActivePageRegistration(onboardingDisplayNamePageInfo);

	const { user: authUser } = React.useContext(AuthenticationContext);
	const { data } = useUserDetailQuery({ variables: { id: authUser?.id ?? "0" }, skip: !authUser });
	const user = data?.user;

	if (!user) {
		return <OnboardingLayout page="display-name">Loading...</OnboardingLayout>;
	}

	return <LoadedContent user={user} />;
}

const userProfileFields = {
	publicNameFormat: true,
	// hidden
	givenName: true,
	familyName: true,
	nameOrder: true,
} as const;

const internalFields = ["givenName", "familyName", "nameOrder"] as (keyof typeof userProfileFields)[];

type LoadedContentProps = {
	user: UserDetailQuery["user"];
};

function LoadedContent({ user }: LoadedContentProps) {
	const history = useHistory();
	const onSuccess = React.useCallback(() => {
		history.push(onboardingNotificationsPageInfo.to);
	}, [history]);
	const { formState, onSubmit } = UserForm.useDetailForm({
		user,
		onSuccess,
		fields: userProfileFields,
		editMode: EditMode.WriteOnly,
	});

	useInternalFields(formState, internalFields);

	return (
		<OnboardingLayout
			page="display-name"
			footerContent={
				<Row justify="space-between">
					<ButtonLink variant="tertiary" to={onboardingProfilePageInfo.to}>
						Back
					</ButtonLink>
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Continue
					</SubmitButton>
				</Row>
			}
		>
			<Form
				formState={formState}
				name={`onboarding.displayName.${user.id}`}
				onSubmit={formState.onSubmitWrapper(onSubmit)}
				noNotifications
				noNavigationPrompt
				noChangesDialog
				mergeUnsavedChangesAutomatically
			>
				<Form.Column verticalSpacing="1.5rem" style={{ margin: "0 auto" }}>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Heading.H2 noMargin>
							How would you like your name to appear to others in the SEE Learning Portal?
						</Heading.H2>

						<Paragraph>
							Your profile will be displayed to other members of the SEE Learning Portal including members within your
							organization and those from other institutions.
						</Paragraph>
					</Column>

					<UserField.PublicNameFormat formState={formState} noHeader />
				</Form.Column>
			</Form>
		</OnboardingLayout>
	);
}
